"use client";

import { useState } from "react";
import {
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CollisionInputs,
  CollisionResults,
  type CollisionState,
} from "@/components/tools/physics-mechanics/collision-panel";
import {
  RelationInputs,
  RelationResults,
} from "@/components/tools/physics-mechanics/relation-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  formatSig,
  SIG_FIG_CHOICES,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import {
  IMPULSE_MOMENTUM,
  type ImpulseKey,
  LINEAR_MOMENTUM,
  type MomentumKey,
} from "@/lib/tool-kit/physics-mechanics/momentum";
import {
  evaluateRelation,
  initialRelationState,
} from "@/lib/tool-kit/physics-mechanics/relation";

/** English strings. A translated content file overrides these through `ui`. */
export const MOMENTUM_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeMomentum: "Momentum (p = mv)",
  modeImpulse: "Impulse and change in momentum",
  modeCollision: "A collision between two objects",
  momentumHint:
    "Momentum is mass times velocity, and it has a direction. Give a velocity in the opposite direction as a negative number and the momentum comes out negative.",
  impulseHint:
    "Impulse is force times the time it acts, and it equals the change in momentum: FΔt = m(v − u), where u is the velocity before and v after. A bounce reverses the velocity, so v is negative if u is positive.",
  collisionHint:
    "A head-on collision along a line. Object 1 starts on the left and moves faster to the right than object 2 — otherwise they never meet.",
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  calculated: "Calculated",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  momentum: "Momentum p",
  mass: "Mass m",
  velocity: "Velocity v",
  force: "Average force F",
  time: "Time the force acts Δt",
  before: "Velocity before u",
  after: "Velocity after v",
  impulse: "Impulse J = FΔt",
  momentumChange: "Change in momentum Δp",
  kineticEnergy: "Kinetic energy, p² ÷ 2m",
  workingHeading: "The working",
  stepEquation: "Start from {equation}.",
  stepRearrange: "Rearrange for {symbol}: {form}.",
  stepConvert: "Convert to SI units: {list}.",
  stepSubstitute: "{symbol} = {working} = {value}",
  stepAnswerUnit: "In the unit you chose: {si} = {shown}.",
  copy: "Copy result",
  copied: "Copied!",
  enterAll: "Enter every value except the one you are solving for.",
  unreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign, or scientific notation such as 1.5e3.",
  errors: {
    "mass-not-positive": "Mass must be greater than zero.",
    "mass-sign":
      "The momentum and the velocity must point the same way (have the same sign) to give a positive mass.",
    "speed-zero":
      "At zero velocity the momentum is zero, so the mass cannot be found. Enter a velocity that is not zero.",
    "time-negative": "Time cannot be negative.",
    "time-zero":
      "The time is zero, so the average force is undefined. Enter a time above zero.",
    "force-zero":
      "With no force there is no impulse, so the time cannot be found. Enter a force that is not zero.",
    "direction-mismatch":
      "The force must point the same way as the change in velocity. Check the signs of the force, u and v.",
    "no-velocity-change":
      "The velocity does not change, so the mass cannot be found from the impulse.",
    "not-finite":
      "The result is too large to calculate. Check the numbers and units.",
  },
  collisionType: "Type of collision",
  typeElastic: "Elastic (bounce, no energy lost)",
  typeInelastic: "Perfectly inelastic (stick together)",
  typePartial: "In between (coefficient of restitution)",
  restitution: "Coefficient of restitution e",
  restitutionHint: "0 means they stick together, 1 means a perfect bounce.",
  mass1: "Mass of object 1, m₁",
  speed1: "Velocity of object 1 before, u₁",
  mass2: "Mass of object 2, m₂",
  speed2: "Velocity of object 2 before, u₂",
  collisionPositive:
    "Velocities to the right are positive and to the left negative.",
  after1: "Velocity of object 1 after",
  after2: "Velocity of object 2 after",
  momentumTotal: "Total momentum (before and after)",
  keBefore: "Kinetic energy before",
  keAfter: "Kinetic energy after",
  keLost: "Kinetic energy lost",
  stepMomentum:
    "Total momentum before: m₁u₁ + m₂u₂ = {m1} × {u1} + {m2} × {u2} = {p} kg·m/s. Momentum is conserved in every collision.",
  stepElastic:
    "Elastic: kinetic energy is conserved too, so v₁ = ((m₁ − m₂)u₁ + 2m₂u₂) ÷ (m₁ + m₂) = {v1} m/s and v₂ = ((m₂ − m₁)u₂ + 2m₁u₁) ÷ (m₁ + m₂) = {v2} m/s, using m₁ = {m1} kg, u₁ = {u1} m/s, m₂ = {m2} kg and u₂ = {u2} m/s.",
  stepInelastic:
    "Perfectly inelastic: the objects move off together at v = (m₁u₁ + m₂u₂) ÷ (m₁ + m₂) = {p} ÷ {total} = {v} m/s.",
  stepPartial:
    "With e = {e}, the speed of separation is e times the speed of approach: v₂ − v₁ = e × (u₁ − u₂) = e × {approach} m/s. Together with conservation of momentum this gives v₁ = {v1} m/s and v₂ = {v2} m/s.",
  stepEnergy:
    "Kinetic energy: before = ½m₁u₁² + ½m₂u₂² = {before} J; after = ½m₁v₁² + ½m₂v₂² = {after} J; lost = {lost} J, which is {share}% of the original.",
  copyText: "After the collision: object 1 at {v1}, object 2 at {v2}",
  collisionNeedAll: "Enter both masses and both velocities.",
  collisionUnreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign.",
  collisionErrors: {
    mass1: "The mass of object 1 must be greater than zero.",
    mass2: "The mass of object 2 must be greater than zero.",
    restitution: "The coefficient of restitution must be between 0 and 1.",
    "no-collision":
      "These velocities mean object 1 never catches up with object 2, so there is no collision. Give object 1 a larger velocity (further right) than object 2.",
  },
  assumptions:
    "Assumes the objects are point-like and move along one line, with no external horizontal force during the collision. Momentum is conserved in every collision; kinetic energy only in elastic ones.",
} as const;

type Mode = "momentum" | "impulse" | "collision";

const INITIAL_MOMENTUM = initialRelationState<MomentumKey>(
  LINEAR_MOMENTUM,
  "p",
  {
    m: { value: "1500", unit: "kg" },
    v: { value: "90", unit: "kph" },
  }
);
const INITIAL_IMPULSE = initialRelationState<ImpulseKey>(
  IMPULSE_MOMENTUM,
  "F",
  {
    t: { value: "0.01", unit: "s" },
    m: { value: "0.43", unit: "kg" },
    u: { value: "0", unit: "mps" },
    v: { value: "25", unit: "mps" },
  }
);
const INITIAL_COLLISION: CollisionState = {
  type: "elastic",
  restitution: "0.5",
  m1: { value: "2", unit: "kg" },
  u1: { value: "3", unit: "mps" },
  m2: { value: "1", unit: "kg" },
  u2: { value: "0", unit: "mps" },
};

export default function MomentumCalculatorWidget() {
  const strings = useToolUi(MOMENTUM_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("momentum");
  const [momentum, setMomentum] = useState(INITIAL_MOMENTUM);
  const [impulse, setImpulse] = useState(INITIAL_IMPULSE);
  const [collision, setCollision] = useState(INITIAL_COLLISION);
  const [sigFigs, setSigFigs] = useState("4");
  const sig = Number(sigFigs);
  const panel = {
    ...strings,
    errors: strings.errors as Record<string, string>,
  };
  const common = { fmt, sig, strings: panel };
  const momentumLabels: Record<MomentumKey, string> = {
    p: strings.momentum,
    m: strings.mass,
    v: strings.velocity,
  };
  const impulseLabels: Record<ImpulseKey, string> = {
    F: strings.force,
    t: strings.time,
    m: strings.mass,
    u: strings.before,
    v: strings.after,
  };
  const momentumEval = evaluateRelation(LINEAR_MOMENTUM, momentum);
  const impulseEval = evaluateRelation(IMPULSE_MOMENTUM, impulse);
  const hints = {
    momentum: strings.momentumHint,
    impulse: strings.impulseHint,
    collision: strings.collisionHint,
  };

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="mo-mode"
        onChange={setMode}
        options={[
          { value: "momentum", label: strings.modeMomentum },
          { value: "impulse", label: strings.modeImpulse },
          { value: "collision", label: strings.modeCollision },
        ]}
        value={mode}
      />
      <p className={HINT}>{hints[mode]}</p>
      {mode === "momentum" ? (
        <RelationInputs
          {...common}
          def={LINEAR_MOMENTUM}
          evaluation={momentumEval}
          idPrefix="mo-p"
          labels={momentumLabels}
          onState={setMomentum}
          state={momentum}
        />
      ) : null}
      {mode === "impulse" ? (
        <RelationInputs
          {...common}
          def={IMPULSE_MOMENTUM}
          evaluation={impulseEval}
          idPrefix="mo-j"
          labels={impulseLabels}
          onState={setImpulse}
          state={impulse}
        />
      ) : null}
      {mode === "collision" ? (
        <CollisionInputs
          onState={setCollision}
          state={collision}
          strings={strings}
        />
      ) : null}
      <div className="w-60">
        <SelectField
          id="mo-sig"
          label={strings.rounding}
          onChange={setSigFigs}
          options={SIG_FIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigFigs, locale, Number(choice), {
              count: fmt(Number(choice)),
            }),
          }))}
          value={sigFigs}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "momentum" ? (
          <RelationResults
            {...common}
            def={LINEAR_MOMENTUM}
            evaluation={momentumEval}
            extras={(all) => (
              <StatGrid>
                <StatCard
                  label={strings.kineticEnergy}
                  value={withUnit(
                    formatSig(fmt, (all.p * all.p) / (2 * all.m), sig),
                    "J"
                  )}
                />
              </StatGrid>
            )}
            labels={momentumLabels}
            state={momentum}
          />
        ) : null}
        {mode === "impulse" ? (
          <RelationResults
            {...common}
            def={IMPULSE_MOMENTUM}
            evaluation={impulseEval}
            extras={(all) => (
              <StatGrid>
                <StatCard
                  label={strings.impulse}
                  value={withUnit(formatSig(fmt, all.F * all.t, sig), "N·s")}
                />
                <StatCard
                  label={strings.momentumChange}
                  value={withUnit(
                    formatSig(fmt, all.m * (all.v - all.u), sig),
                    "kg·m/s"
                  )}
                />
              </StatGrid>
            )}
            labels={impulseLabels}
            state={impulse}
          />
        ) : null}
        {mode === "collision" ? (
          <>
            <CollisionResults
              fmt={fmt}
              sig={sig}
              state={collision}
              strings={strings}
            />
            <p className={HINT}>{strings.assumptions}</p>
          </>
        ) : null}
      </section>
    </>
  );
}
